import { takeEvery, put, call, all, Effect, select, delay, takeLeading, cancel } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { userSetupSlice } from "../reducer/user_setup";
import { goForward, push, replace } from "redux-first-history";
import { stateApi } from "../../api/StateApi";
import { ApiResponse } from "../../model/api";
import { taxApi } from "../../api/TaxApi";
import { stateSlice, StateState } from "../reducer/state";
import { PersistedState } from "../../model/persisted_state";
import { RootState } from "..";
import { TaxReturnStatusResponse, TaxReturnStatus } from "../../model/tax/tax_return";
import { Location } from "history";
import { TaxDocumentStatus } from "../../enums/tax";
import { TaxSeasonPayload } from "../../model/season";
import * as Sentry from '@sentry/react';
import { ApiError, ApiErrorInterface, NotFoundError, MissingFrontEndStateError, SerializedApiError} from "../../model/errors/errors";

function _determineLocationFromSavedLocationAndState(savedLocation: string | null | undefined, state: StateState) {
  // See comment below about enabling year-round payments for users with completed tax returns
  if (state.backendState.status === TaxDocumentStatus.Complete || savedLocation === '/user/sorry') {
    return '/user/welcome-back';
  }

  if (savedLocation?.startsWith('/upload')) {
    return '/upload/IRS1040';
  }

  if (state.backendState.status === TaxDocumentStatus.AwaitingSignature) {
    return '/balance';
  }

  /*
   * I think it would be rad that anytime a user who have signed their
   * return can log back into Lime and make a payment/choose refund now that returns
   * are considered 'completed' after signing and not after payment.
   *
   * However, there is at least one edge case here: users you already paid/submitted refund
   * and they log back in during same tax season they will re-enter the payment flow.
   * Frontend ideally needs a boolean sent from backend on state endpoint saying whether or not
   * there are unmet financial requirements.
   *
   * Regardless, the component at /transition/post-signature is already ready to handle users with
   * completed returns logging back in to Lime and redirecting them to the correct payment/refund flow.
  if (state.backendState.status === TaxDocumentStatus.Complete) {
    return '/transition/post-signature';
  }
  */

  // TODO: redirect for Error state

  // This doesn't make sense to push the user to since
  // that page has most of its context from the welcome page
  if (savedLocation === '/user/update-address') {
    return '/user/welcome-back';
  }

  return savedLocation || '/user/welcome-back';
}

/*
 * Pull frontend and backend states and then shuttle
 * off user to appropriate place in the tax filing flow.
 *
 * Note that we always want to attempt redirecting based on backend tax return state
 * because a spouse could have gotten a tax return newly attached to them, and
 * not shooting them off to where the state is set will cause a ton of issues
 *
 * TODO? It would be nice in the future if frontend/backend were
 * separated into distinct sagas, and the backend state saga
 * was set to run upon each container mount 
 */
function* fetchStateSaga(): Generator<Effect, void, any> {
  try {
    const frontendStateResponse = yield call(stateApi.getState);
    let persistedState: PersistedState = JSON.parse(frontendStateResponse.data.state);
    const backendStateResponse: ApiResponse<TaxReturnStatusResponse> = yield call(taxApi.fetchTaxReturn);
    let taxReturnPayload = backendStateResponse.data.tax_return_status as TaxReturnStatus;
    yield put(stateSlice.actions.fetchStateSuccess(taxReturnPayload));
    yield put(stateSlice.actions.setTaxYear(frontendStateResponse.data.tax_year));
    yield put(stateSlice.actions.fetchFilingDate());
    if (persistedState.location) {
      const basePath = persistedState.location.split('/')[0];
      if (basePath !== 'balance' && basePath !== 'payment') {
        // This can melt a computer in certain states...
        for (let err of persistedState.userSetup.stepErrors) {
          yield put(userSetupSlice.actions.addSubstepError(err));
        }
      }
    }
    let state: RootState = yield select();
    // Very important to not add any of our redirection logic for the payment complete page
    // This is because this is the URL that Stripe will redirect to after a successful payment.
    if ((state.router.location as Location).pathname !== '/payment/complete') {
      yield put(push(_determineLocationFromSavedLocationAndState(persistedState.location, state.state)));
    }
  } catch (error: SerializedApiError<any> | any) {
    // On first sign in there will be no frontend state record and backend sends this error message.
    // Persist an initial state and then pull backend state state again (since it got interrupted above)
    if (ApiError.deserialize(error) instanceof MissingFrontEndStateError) {
      try {
        yield put(stateSlice.actions.persistInitialState());
        const backendStateResponse: ApiResponse<TaxReturnStatusResponse> = yield call(taxApi.fetchTaxReturn);
        const taxReturnPayload = backendStateResponse.data.tax_return_status as TaxReturnStatus;
        yield put(stateSlice.actions.fetchStateSuccess(taxReturnPayload));

        let state: RootState = yield select();
        yield put(push(_determineLocationFromSavedLocationAndState(null, state.state)));
      } catch (error: any) {
        let persistenceError = new Error("Fetch state saga failed after initial frontend state persistence.");
        Sentry.captureException(persistenceError);
        yield put(stateSlice.actions.cancelFetchState());
      }
    } else {
      Sentry.captureException(ApiError.deserialize(error) ?? error);
      const err = ApiError.deserialize(error)?.toString() ?? String(error);
      yield put(stateSlice.actions.cancelFetchState());
      yield put(replace('/user/sorry', { state: { error: err }}));
      yield put(stateSlice.actions.fetchStateError(err));
    }
  }
}

function* fetchFilingDateSaga(): Generator<Effect, void, any> {
  const state: StateState = yield select((state) => state.state);
  if (state.season || state.frontendState.error !== null) {
    return;
  }
  try {
    const response = yield call(stateApi.getFilingDate);
    const season = response.data as TaxSeasonPayload;
    yield put(stateSlice.actions.fetchFilingDateSuccess(season));
    if (!season.filingIsOpen) {
      yield put(replace('/user/filing-closed'));
    }
  } catch (error: SerializedApiError<any> | any) {
    if (ApiError.deserialize(error) instanceof NotFoundError) {
      // Backend returns a 404 if admin did not set filing dates
      // for given tax year, which would fall into this block.
      yield put(replace('/user/filing-closed'));
    } else {
      yield put(replace('/user/sorry', { state: { error: error }}));
    }
    
    yield put(stateSlice.actions.fetchStateError(ApiError.deserialize(error)?.toString() ?? String(error)));
  }
}

function* pollBackendStateChangeSaga(action: PayloadAction<TaxDocumentStatus>):  Generator<Effect, void, any> {
  yield delay(300); // Give backend half a second to finish transitioning tax return states
  let desiredState = action.payload;
  let apiCalls = 0;
  try {
    const backendStateResponse: ApiResponse<TaxReturnStatusResponse> = yield call(taxApi.fetchTaxReturn);
    const taxReturnPayload = backendStateResponse.data.tax_return_status as TaxReturnStatus;
    let taxReturnState = taxReturnPayload.state;
    // Query for approx: 28 seconds
    while (desiredState != taxReturnState && apiCalls < 3) {
      apiCalls += 1;
      const backendStateResponse: ApiResponse<TaxReturnStatusResponse> = yield call(taxApi.fetchTaxReturn);
      const taxReturnPayload = backendStateResponse.data.tax_return_status as TaxReturnStatus;
      taxReturnState = taxReturnPayload.state;
      yield delay(4000);
    }
    yield put(stateSlice.actions.fetchStateSuccess(taxReturnPayload)); // Update final state

    // If frontend and backend state match, save as a checkpoint, otherwise set an error
    if (desiredState == taxReturnState) {
      yield put(stateSlice.actions.saveCheckpoint(taxReturnState));
    } else {
      yield put(stateSlice.actions.fetchStateError('The anticipated tax return status did not change.'));
    }
  } catch (error: SerializedApiError<any> | any) {
    yield put(stateSlice.actions.fetchStateError('The anticipated tax return status did not change.'));
  }
}

function* sendToRouteWithDelaySaga(action: PayloadAction<string>): Generator<Effect, void, any> {
  yield delay(4000);
  yield put(push(action.payload));
}

function* persistInitialStateSaga(): Generator<Effect, void, any> {
  let state = yield select();
  const location = state.router.location.pathname;
  const stateLocation = location == '/user/filing-closed' ? '/user' : location;
  let stateToPersist: PersistedState = {
    'location': stateLocation,
    'userSetup': { 'stepErrors': state.userSetup.stepErrors },
  };
  try {
    let statePayload = { 'state': JSON.stringify(stateToPersist)};
    const persistedStateResponse = yield call(stateApi.createState, statePayload);
    yield put(stateSlice.actions.setTaxYear(persistedStateResponse.data.tax_year));
    yield put(stateSlice.actions.fetchFilingDate());
    yield put(stateSlice.actions.persistInitialStateSuccess());
  } catch (error: SerializedApiError<any> | any) {
    yield put(stateSlice.actions.persistInitialStateError(
      ApiError.deserialize(error)?.toString() ?? error.message ?? String(error)
    ));
  }
}

function* updatePersistedStateSaga(): Generator<Effect, void, any> {
  try {
    let state: RootState = yield select();
    const location = state.router.location?.pathname;
    const stateLocation = location == '/user/filing-closed' ? '/user' : location; 
    let stateToPersist: PersistedState = {
      'location': stateLocation,
      'userSetup': { 'stepErrors': state.userSetup.stepErrors },
    };
    let statePayload = { 'state': JSON.stringify(stateToPersist)};
    yield call(stateApi.updateState, statePayload);

    yield put(stateSlice.actions.updatePersistedStateSuccess());
  } catch (error: SerializedApiError<any> | any) {
    yield put(stateSlice.actions.updatePersistedStateError(
      ApiError.deserialize(error)?.toString() ?? error.message ?? String(error)
    ));
  }
}

export default function* stateSaga() {
  yield all([takeEvery(stateSlice.actions.fetchState, fetchStateSaga)]);
  yield all([takeEvery(stateSlice.actions.fetchFilingDate, fetchFilingDateSaga)]);  
  yield all([takeEvery(stateSlice.actions.sendToRouteWithDelay, sendToRouteWithDelaySaga)]);
  yield all([takeEvery(stateSlice.actions.pollBackendStateChange, pollBackendStateChangeSaga)]);
  yield all([takeEvery(stateSlice.actions.updatePersistedState, updatePersistedStateSaga)]);
  yield all([takeEvery(stateSlice.actions.persistInitialState, persistInitialStateSaga)]);
}
